import { DayMode, EngineErrorBody } from '@services/engineService';

/**
 * PRD 6 §6 — the three feasibility errors are shown in different places and are **not**
 * alternatives: `MISSING_DAYS` belongs on the offending row, `MISSING_BRICK_DAY` on the brick
 * block, `INFEASIBLE_WEEK` under the grid.
 */
export interface FeasibilityMessages {
  /** sportId → the message for that sport's row. */
  perSport: Record<string, string>;
  brick: string | null;
  global: string | null;
}

export const NO_MESSAGES: FeasibilityMessages = { perSport: {}, brick: null, global: null };

/**
 * N per sport **including PPG**, which is a switch rather than a stepper and so never appears in
 * `sessionsPerWeek`. Without it a `MISSING_DAYS` on PPG reads « Ouvre au moins 0 jour pour caser tes
 * 0 séance ». PPG is fixed at one session a week (F2, `PPG_SESSIONS_PER_WEEK`).
 */
export const effectiveSessionsPerWeek = (
  sessionsPerWeek: Record<string, number>,
  includePpg: boolean,
  ppgSportId: string
): Record<string, number> => ({ ...sessionsPerWeek, [ppgSportId]: includePpg ? 1 : 0 });

/** R37: a `DAYS_ONLY` plan has no session count to quote — only its two days per discipline. */
export const feasibilityMessages = (
  error: EngineErrorBody | null,
  sessionsPerWeek: Record<string, number>,
  dayMode?: DayMode
): FeasibilityMessages => {
  if (!error) return NO_MESSAGES;
  const daysOnly = dayMode === 'DAYS_ONLY';

  if (error.code === 'MISSING_DAYS') {
    const perSport: Record<string, string> = {};
    for (const sportId of error.sportIds ?? []) {
      if (daysOnly) {
        perSport[sportId] = 'Coche au moins 2 jours.';
        continue;
      }
      const n = sessionsPerWeek[sportId] ?? 0;
      perSport[sportId] =
        n === 1 ? 'Ouvre au moins 1 jour pour caser ta séance.' : `Ouvre au moins ${n} jours pour caser tes ${n} séances.`;
    }
    return { ...NO_MESSAGES, perSport };
  }

  if (error.code === 'MISSING_BRICK_DAY') {
    return { ...NO_MESSAGES, brick: 'Choisis le jour de ton enchaînement.' };
  }

  if (error.code === 'INFEASIBLE_WEEK') {
    return {
      ...NO_MESSAGES,
      global: daysOnly
        ? "Ajoute des jours disponibles pour qu'on puisse placer toutes tes séances."
        : "Ajoute des jours disponibles pour qu'on puisse placer toutes tes séances (ou retire des séances à l'étape précédente)",
    };
  }

  // Anything else (INVALID_VOLUME…) already comes with the server's own wording.
  return { ...NO_MESSAGES, global: error.message ?? 'Ces réglages ne sont pas réalisables.' };
};
